/**
 * Dialog for saving template with "Save As" functionality
 * Shows input field for new name + list of existing templates to overwrite
 */

import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog } from "primereact/confirmdialog";
import { Tooltip } from "primereact/tooltip";
import PropTypes from "prop-types";
import ModalBase from "../../components/Modals/ModalBase";
import "./DesktopManager.css";

const SaveAsTemplateDialog = ({ visible, onHide, onSave, onRefresh }) => {
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Load templates when dialog opens
  useEffect(() => {
    if (visible) {
      setTemplateName("");
      setError("");
      loadTemplates();
    }
  }, [visible]);

  const loadTemplates = async () => {
    setLoading(true);
    setError("");

    try {
      const list = await onRefresh();
      setTemplates(list || []);
    } catch (err) {
      console.error("Error loading templates:", err);
      setError(err.message || "Falha ao carregar lista de templates");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const validateName = (name) => {
    if (!name || name.trim() === "") {
      return "Nome do template é obrigatório";
    }
    if (name.trim().length < 3) {
      return "Nome deve ter pelo menos 3 caracteres";
    }
    if (name.trim().length > 50) {
      return "Nome deve ter no máximo 50 caracteres";
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateName(templateName);
    if (validationError) {
      setError(validationError);
      return;
    }

    const trimmedName = templateName.trim();

    // Check if name already exists
    const existingTemplate = templates.find(
      (t) => t.name.toLowerCase() === trimmedName.toLowerCase(),
    );

    if (existingTemplate) {
      // Ask for confirmation to overwrite
      confirmDialog({
        message: `Já existe um template com o nome "${trimmedName}". Deseja sobrescrever?`,
        header: "Confirmar Sobrescrita",
        icon: "pi pi-exclamation-triangle",
        acceptClassName: "p-button-warning",
        acceptLabel: "Sobrescrever",
        rejectLabel: "Cancelar",
        accept: async () => {
          await performSave(trimmedName, existingTemplate.id);
        },
      });
    } else {
      // New name - save directly
      await performSave(trimmedName);
    }
  };

  const performSave = async (name, existingId = null) => {
    setSaving(true);
    setError("");

    try {
      await onSave(name, existingId);
      onHide(); // Close dialog on success
    } catch (err) {
      console.error("Error saving template:", err);
      setError(err.message || "Falha ao salvar template");
    } finally {
      setSaving(false);
    }
  };

  const handleOverwrite = (template) => {
    // Fill the input with the template name
    setTemplateName(template.name);

    // Show confirmation
    confirmDialog({
      message: `Deseja sobrescrever o template "${template.name}"?`,
      header: "Confirmar Sobrescrita",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-warning",
      acceptLabel: "Sobrescrever",
      rejectLabel: "Cancelar",
      accept: async () => {
        await performSave(template.name, template.id);
      },
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !saving) {
      handleSave();
    }
  };

  const formatDate = (timestamp) => {
    try {
      let date;
      if (typeof timestamp === "number" && timestamp < 4102444800000) {
        date = new Date(timestamp * 1000);
      } else {
        date = new Date(timestamp);
      }

      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  // Column templates
  const nameTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
        <i
          className="pi pi-palette"
          style={{ fontSize: "12px", color: "var(--primary-color)" }}
        ></i>
        <span
          style={{
            fontSize: "12px",
            fontWeight: "500",
            color: "var(--text-color)",
          }}
        >
          {rowData.name}
        </span>
      </div>
    );
  };

  const dateTemplate = (rowData) => {
    return (
      <span
        style={{
          fontSize: "11px",
          color: "var(--text-color-secondary)",
        }}
      >
        {formatDate(rowData.timestamp)}
      </span>
    );
  };

  const actionsTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", gap: "1px", justifyContent: "center" }}>
        <Button
          icon="pi pi-save"
          className="p-button-text p-button-sm p-button-warning template-action-btn-overwrite"
          onClick={() => handleOverwrite(rowData)}
          data-pr-tooltip="Sobrescrever"
          data-pr-position="top"
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id={`saveastemplatedialog-btn-overwrite-${rowData.id}`}
        />
      </div>
    );
  };

  const headerWithSearch = (
    <div className="desktop-dialog-header-inline">
      <span className="desktop-dialog-title">Salvar Template Como</span>
      <div className="desktop-header-search">
        <i className="pi pi-search" />
        <input
          type="text"
          autoComplete="off"
          className="p-inputtext p-component"
          placeholder="Buscar..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          data-evo-id="saveastemplatedialog-input-filter"
        />
      </div>
      <Button
        icon="pi pi-refresh"
        className="p-button-text p-button-sm p-button-rounded template-refresh-btn"
        onClick={loadTemplates}
        disabled={loading}
        data-pr-tooltip="Atualizar lista"
        data-pr-position="top"
        style={{ width: "26px", height: "26px", padding: "0" }}
        data-evo-id="saveastemplatedialog-btn-refresh"
      />
    </div>
  );

  const emptyMessage = (
    <div className="desktop-empty-state">
      <i className="pi pi-inbox"></i>
      <p>
        {globalFilter
          ? "Nenhum template encontrado com esse nome"
          : "Nenhum template salvo encontrado"}
      </p>
    </div>
  );

  return (
    <>
      <Tooltip target=".template-action-btn-overwrite" />
      <Tooltip target=".template-refresh-btn" />

      <ModalBase
        header={headerWithSearch}
        visible={visible}
        onHide={onHide}
        size="large"
        variant="data"
        footer={null}
        closable={!saving}
        className="desktop-load-dialog-modal save-as-template-dialog"
      >
        <div className="desktop-load-dialog">
          {/* Name Input Section */}
          <div
            className="save-as-name-section"
            style={{
              padding: "0.375rem 0.5rem",
            }}
          >
            <label
              htmlFor="template-name-input"
              style={{
                display: "block",
                marginBottom: "0.375rem",
                fontWeight: "600",
                fontSize: "12px",
              }}
            >
              Nome do Template
            </label>
            <div
              className="save-as-input-row"
              style={{
                display: "flex",
                gap: "0.375rem",
                alignItems: "flex-start",
              }}
            >
              <div style={{ flex: 1 }}>
                <InputText
                  id="template-name-input"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Nome do novo template"
                  autoFocus
                  disabled={saving}
                  style={{ width: "100%", fontSize: "0.8125rem" }}
                  data-evo-id="saveastemplatedialog-input-name"
                />
                <small
                  style={{
                    display: "block",
                    marginTop: "0.125rem",
                    color: "var(--text-color-secondary)",
                    fontSize: "10px",
                  }}
                >
                  Ou selecione um existente abaixo para sobrescrever
                </small>
              </div>
              <Button
                label="Salvar"
                icon="pi pi-check"
                onClick={handleSave}
                disabled={saving || !templateName.trim()}
                loading={saving}
                style={{ fontSize: "0.8125rem" }}
                data-evo-id="saveastemplatedialog-btn-save"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <Message
              severity="error"
              text={error}
              className="desktop-error-message"
            />
          )}

          {/* Template Table */}
          {loading ? (
            <div className="desktop-loading-state">
              <ProgressSpinner style={{ width: "50px", height: "50px" }} />
              <p>Carregando templates...</p>
            </div>
          ) : (
            <DataTable
              value={templates}
              globalFilter={globalFilter}
              emptyMessage={emptyMessage}
              scrollable
              scrollHeight="300px"
              showGridlines
              stripedRows
              size="small"
              className="desktop-table"
            >
              <Column
                field="name"
                header="Nome"
                body={nameTemplate}
                sortable
                headerClassName="desktop-col-name"
                bodyClassName="desktop-col-name"
              />
              <Column
                field="timestamp"
                header="Data"
                body={dateTemplate}
                sortable
                style={{ width: "130px" }}
                headerClassName="desktop-col-date"
                bodyClassName="desktop-col-date"
              />
              <Column
                header="Ações"
                body={actionsTemplate}
                style={{ width: "80px", textAlign: "center" }}
                headerClassName="desktop-col-actions"
                bodyClassName="desktop-col-actions"
              />
            </DataTable>
          )}
        </div>
      </ModalBase>
    </>
  );
};

SaveAsTemplateDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

export default SaveAsTemplateDialog;
